import React, { useState } from "react";
import { C, font, inp, lbl, btnS } from "../../../../lib/styles.js";
import { useData } from "../../../../context/DataContext.jsx";
import { fmtDate, fmtTime, FMT_DATE_DOW, addressMapQuery } from "../../../../lib/utils.js";

/* ═══════════════════════════════════════════════════════════════
   JobPrepTab — day-of-shoot prep view
   Props: { job, editing, isCrew, canEditShootWork, canManageSettings }
   Job-level fields (equipment, transport, crew, prepNotes, lenses,
   additionalItems) are read/written directly. Session-scoped date +
   location are read from the projected `job` (scopedJob) passed by
   JobDetailStub.
═══════════════════════════════════════════════════════════════ */

export default function JobPrepTab({ job, editing, isCrew, canEditShootWork, canManageSettings, updateJobFields }) {
  const { studioSettings, setStudioSettings, updateJob } = useData();
  const equipment    = studioSettings.equipment    || {};
  const crewRoster   = studioSettings.crewRoster   || [];
  const storageOpts  = studioSettings.storageOpts  || [];
  const transportOpts= studioSettings.transportOpts|| [];

  const [hov, setHov] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [open, setOpen] = useState(true); // collapsible Job Prep card

  const selectedEquip = new Set(job.selectedEquipment || job.equipment || []);
  const transport = job.transport || "In Studio";
  const crew = Array.isArray(job.crew)
    ? job.crew
    : (job.crew ? job.crew.split(",").map(s => s.trim()).filter(Boolean) : []);
  const prepNotes = job.prepNotes || "";
  const lenses = job.lenses || [];
  const additionalItems = job.additionalItems || [];
  const eqCount = selectedEquip.size;

  const upd = (field, val) => {
    if (updateJobFields) updateJobFields({ [field]: val });
    else updateJob(job.id, { [field]: val });
  };
  const toggleEquip = (name) => {
    const next = new Set(selectedEquip);
    next.has(name) ? next.delete(name) : next.add(name);
    upd("selectedEquipment", [...next]);
  };
  const toggleCrew = (name) =>
    upd("crew", crew.includes(name) ? crew.filter(n => n !== name) : [...crew, name]);

  const mapQuery = addressMapQuery(job);
  const isOnLocation = !job.isStudio && mapQuery && mapQuery !== "In Studio";

  // Admin gets shoot-work controls when in edit mode; crew can toggle equipment/crew/transport anytime.
  const canEdit = canEditShootWork && (editing || isCrew);

  /* ══ Brief strip ══ */
  const briefDate = job.captureDate
    ? (fmtDate(job.captureDate, FMT_DATE_DOW) + (job.callTime ? " · " + fmtTime(job.callTime) : ""))
    : "TBD";

  return (
    <div style={{ fontFamily: font }}>
      <style>{`@keyframes slideIn{from{transform:translateX(100%)}to{transform:translateX(0)}}`}</style>

      {canManageSettings && (
        <SettingsPanel
          isOpen={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          studioSettings={studioSettings}
          setStudioSettings={setStudioSettings}
        />
      )}

      {/* Controls */}
      {canManageSettings && (
        <div style={S.controlsRow}>
          <button onClick={() => setSettingsOpen(true)} style={S.settingsBtn}>
            <SettingsIcon />
            Settings
          </button>
        </div>
      )}

      {/* Job Prep card */}
      <div style={S.card}>
        <button type="button" onClick={() => setOpen(o => !o)} style={S.header} aria-expanded={open}>
          <span style={{ fontSize: 13, fontWeight: 500, color: C.text }}>Job Prep</span>
          <span style={{ color: C.muted, fontSize: 10, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}>▾</span>
        </button>
        <div style={{ background: C.surface, display: open ? "block" : "none", borderTop: `1px solid ${C.border}` }}>
            {/* Brief strip */}
            <div className="jp-brief" style={S.briefStrip}>
              {[
                { l: "Date",      v: briefDate,                                  c: C.text },
                { l: "Equipment", v: eqCount > 0 ? eqCount + " selected" : "—", c: eqCount > 0 ? C.blue : C.muted },
                { l: "Transport", v: transport,                                  c: C.amber },
                { l: "Crew",      v: crew.join(", ") || "—",                     c: crew.length ? C.green : C.muted },
              ].map((item, i) => (
                <div key={i} style={{ ...S.briefCell, borderRight: i < 3 ? `1px solid ${C.border}` : "none" }}>
                  <div style={S.briefLabel}>{item.l}</div>
                  <div style={{ ...S.briefValue, color: item.c }}>{item.v}</div>
                </div>
              ))}
            </div>

            {/* Gear Selection */}
            <div style={{ padding: "20px 18px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <label style={{ ...lbl, marginBottom: 0 }}>Gear Selection</label>
                <span style={{ fontSize: 10, color: eqCount > 0 ? C.blue : C.muted }}>{eqCount} selected</span>
              </div>
              {Object.entries(equipment).map(([group, items]) => {
                const groupSelected = items.filter(e => selectedEquip.has(e.name));
                if (!canEdit && groupSelected.length === 0) return null;
                return (
                  <div key={group} style={{ marginBottom: 16 }}>
                    <div style={S.gearGroupLabel}>{group}</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {items.map(equip => {
                        const needed = selectedEquip.has(equip.name);
                        if (!canEdit && !needed) return null;
                        return (
                          <div
                            key={equip.id}
                            onMouseEnter={() => setHov("eq_" + equip.name)}
                            onMouseLeave={() => setHov(null)}
                            style={{ ...S.gearItem, background: hov === "eq_" + equip.name ? C.surfaceAlt : "transparent" }}
                          >
                            {canEdit ? (
                              <button
                                onClick={() => toggleEquip(equip.name)}
                                style={{ ...S.gearCheckbox, borderColor: needed ? `${C.blue}60` : C.border, background: needed ? `${C.blue}20` : "transparent" }}
                              >
                                {needed && <CheckIcon color={C.blue} />}
                              </button>
                            ) : (
                              <div style={S.gearDot} />
                            )}
                            <span style={{ fontSize: 13, color: needed ? C.text : `${C.muted}AA`, flex: 1, fontWeight: 300 }}>{equip.name}</span>
                            {hov === "eq_" + equip.name && equip.note && (
                              <span style={{ fontSize: 11, color: C.muted }}>{equip.note}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              {eqCount === 0 && !canEdit && (
                <div style={S.emptyHint}>No equipment selected — switch to Edit mode</div>
              )}
            </div>

            <div style={S.divider} />

            {/* Transport + Crew */}
            <div className="jp-tcrow" style={S.tcRow}>
              <div>
                <label style={lbl}>Transport</label>
                {canEdit ? (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
                    {transportOpts.map(t => {
                      const active = transport === t;
                      return (
                        <button
                          key={t}
                          onClick={() => upd("transport", t)}
                          onMouseEnter={() => setHov("tr_" + t)}
                          onMouseLeave={() => setHov(null)}
                          style={{
                            ...S.toggleChip,
                            borderColor: active ? `${C.amber}50` : C.border,
                            background: active ? `${C.amber}15` : hov === "tr_" + t ? C.surfaceAlt : "transparent",
                            color: active ? C.amber : C.muted,
                          }}
                        >{t}</button>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ fontSize: 14, color: C.amber, marginTop: 4, fontWeight: 300 }}>{transport}</div>
                )}
              </div>
              <div>
                <label style={lbl}>Crew</label>
                {canEdit ? (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
                    {crewRoster.map(c => {
                      const name = c.name;
                      if (!name) return null;
                      const active = crew.includes(name);
                      return (
                        <button
                          key={name}
                          onClick={() => toggleCrew(name)}
                          onMouseEnter={() => setHov("cr_" + name)}
                          onMouseLeave={() => setHov(null)}
                          style={{
                            ...S.toggleChip,
                            borderColor: active ? `${C.green}50` : C.border,
                            background: active ? `${C.green}15` : hov === "cr_" + name ? C.surfaceAlt : "transparent",
                            color: active ? C.green : C.muted,
                            display: "flex", alignItems: "center", gap: 6,
                          }}
                        >
                          {active && <CheckIcon color={C.green} />}
                          {name}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
                    {crew.map(name => (
                      <span key={name} style={S.crewChip}>{name}</span>
                    ))}
                    {crew.length === 0 && <span style={S.emptyInlineMuted}>No crew assigned</span>}
                  </div>
                )}
              </div>
            </div>

            <div style={S.divider} />

            {/* Location — display only, entered on Job Card */}
            {isOnLocation && (
              <>
                <div style={S.locationBlock}>
                  <div>
                    <label style={{ ...lbl, marginBottom: 6 }}>Location</label>
                    {job.locationVenue && <div style={S.locationVenue}>{job.locationVenue}</div>}
                    {job.locationAddress && <div style={S.locationLine}>{job.locationAddress}</div>}
                    {(job.locationCity || job.locationState || job.locationZip) && (
                      <div style={S.locationLine}>
                        {[job.locationCity, job.locationState].filter(Boolean).join(", ")}
                        {job.locationZip ? " " + job.locationZip : ""}
                      </div>
                    )}
                    {!job.locationVenue && !job.locationAddress && job.location && (
                      <div style={S.locationVenue}>{job.location}</div>
                    )}
                  </div>
                  <a
                    href={"https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(mapQuery)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={S.mapLink}
                  >
                    <MapPinIcon />
                    Map
                  </a>
                </div>
                <div style={S.divider} />
              </>
            )}

            {/* Lenses + Additional Items + Prep Notes */}
            <div className="jp-twocol" style={S.twoCol}>
              <div style={{ padding: "20px 18px", borderRight: `1px solid ${C.border}` }}>
                <SimpleList
                  items={lenses}
                  onUpdate={v => upd("lenses", v)}
                  label="Lenses"
                  placeholder="e.g. 24mm, 50mm..."
                  editMode={canEdit}
                />
                <div style={{ marginTop: 20 }}>
                  <SimpleList
                    items={additionalItems}
                    onUpdate={v => upd("additionalItems", v)}
                    label="Additional Items"
                    placeholder="e.g. SD cards, ladder..."
                    editMode={canEdit}
                  />
                </div>
              </div>
              <div style={{ padding: "20px 18px" }}>
                <label style={lbl}>Prep Notes</label>
                {canEdit ? (
                  <textarea
                    value={prepNotes}
                    onChange={e => upd("prepNotes", e.target.value)}
                    rows={6}
                    placeholder="Setup instructions, client requirements..."
                    style={{ ...inp, minHeight: 120, lineHeight: 1.6, resize: "vertical" }}
                  />
                ) : (
                  <p style={S.notesBody}>{prepNotes || <span style={{ color: C.muted, fontStyle: "italic" }}>No notes</span>}</p>
                )}
              </div>
            </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SimpleList — removable chip list with add input in edit mode
═══════════════════════════════════════════════════════════════ */
function SimpleList({ items, onUpdate, label, placeholder, editMode }) {
  const [val, setVal] = useState("");
  const [hov, setHov] = useState(null);
  const add = () => {
    const v = val.trim();
    if (!v || items.includes(v)) return;
    onUpdate([...items, v]);
    setVal("");
  };
  const remove = (item) => onUpdate(items.filter(i => i !== item));

  return (
    <div>
      <label style={{ ...lbl, marginBottom: 8 }}>{label}</label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {items.map(item => (
          <div
            key={item}
            onMouseEnter={() => setHov("sl_" + item)}
            onMouseLeave={() => setHov(null)}
            style={S.simpleChip}
          >
            {item}
            {editMode && hov === "sl_" + item && (
              <button onClick={() => remove(item)} style={S.chipClose}>
                <XIcon />
              </button>
            )}
          </div>
        ))}
        {items.length === 0 && !editMode && <span style={S.emptyInlineMuted}>None</span>}
      </div>
      {editMode && (
        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          <input
            value={val}
            onChange={e => setVal(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") add(); }}
            placeholder={placeholder}
            style={{ ...inp, fontSize: 12, padding: "7px 10px" }}
          />
          <button onClick={add} style={{ ...btnS, padding: "7px 12px", fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
            <PlusIcon /> Add
          </button>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SettingsPanel — 440px right-docked slide-over with 5 tabs
═══════════════════════════════════════════════════════════════ */
function SettingsPanel({ isOpen, onClose, studioSettings, setStudioSettings }) {
  const [activeTab, setActiveTab] = useState("equipment");
  const [eqName, setEqName] = useState("");
  const [eqNote, setEqNote] = useState("");
  const [eqCat, setEqCat] = useState("");
  const [newCat, setNewCat] = useState("");
  const [showNewCat, setShowNewCat] = useState(false);
  const [listInput, setListInput] = useState("");
  const [hov, setHov] = useState(null);

  const equipment = studioSettings.equipment || {};
  const crewRoster = studioSettings.crewRoster || [];
  const storageOpts = studioSettings.storageOpts || [];
  const transportOpts = studioSettings.transportOpts || [];
  const roleOpts = studioSettings.roleOpts || [];

  const setEquipment   = fn => setStudioSettings(p => ({ ...p, equipment: typeof fn === "function" ? fn(p.equipment || {}) : fn }));
  const setCrewRoster  = fn => setStudioSettings(p => ({ ...p, crewRoster: typeof fn === "function" ? fn(p.crewRoster || []) : fn }));
  const setStorageOpts = fn => setStudioSettings(p => ({ ...p, storageOpts: typeof fn === "function" ? fn(p.storageOpts || []) : fn }));
  const setTransportOpts = fn => setStudioSettings(p => ({ ...p, transportOpts: typeof fn === "function" ? fn(p.transportOpts || []) : fn }));
  // Crew Card roles (docs/crew-cards/DESIGN.md): the "role on the day" list.
  const setRoleOpts = fn => setStudioSettings(p => ({ ...p, roleOpts: typeof fn === "function" ? fn(p.roleOpts || []) : fn }));

  const categories = Object.keys(equipment);
  const addEquipItem = () => {
    const name = eqName.trim();
    const note = eqNote.trim();
    const cat = showNewCat ? newCat.trim() : eqCat;
    if (!name || !cat) return;
    setEquipment(prev => {
      const next = { ...prev };
      const items = next[cat] ? [...next[cat]] : [];
      if (items.some(i => i.name === name)) return prev;
      items.push({ id: "eq_" + Date.now(), name, note });
      next[cat] = items;
      return next;
    });
    setEqName(""); setEqNote(""); setShowNewCat(false); setNewCat("");
  };
  const removeEquipItem = (cat, id) => setEquipment(prev => {
    const next = { ...prev };
    next[cat] = (next[cat] || []).filter(i => i.id !== id);
    if (next[cat].length === 0) delete next[cat];
    return next;
  });
  const addListItem = (setter) => {
    const val = listInput.trim();
    if (!val) return;
    setter(prev => prev.includes(val) ? prev : [...prev, val]);
    setListInput("");
  };
  const removeListItem = (setter, item) => setter(prev => prev.filter(i => i !== item));

  if (!isOpen) return null;

  const tabs = [
    { key: "equipment", label: "Equipment" },
    { key: "crew",      label: "Crew" },
    { key: "storage",   label: "Storage" },
    { key: "transport", label: "Transport" },
    { key: "roles",     label: "Roles" },
  ];

  return (
    <>
      <div onClick={onClose} style={S.overlay} />
      <div style={S.slideOver}>
        <div style={S.slideHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <SettingsIcon color={C.muted} />
            <span style={{ fontSize: 15, fontWeight: 400, color: C.text }}>Job Prep Settings</span>
          </div>
          <button onClick={onClose} style={S.iconBtn}><XIcon size={18} /></button>
        </div>

        <div style={{ display: "flex", borderBottom: `1px solid ${C.border}` }}>
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setListInput(""); }}
              style={{
                ...S.slideTab,
                background: activeTab === tab.key ? C.surfaceAlt : "transparent",
                color: activeTab === tab.key ? C.text : C.muted,
                borderBottom: activeTab === tab.key ? `2px solid ${C.amber}` : "2px solid transparent",
              }}
            >{tab.label}</button>
          ))}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "20px 22px" }}>
          {activeTab === "equipment" && (
            <div>
              {categories.map(cat => (
                <div key={cat} style={{ marginBottom: 22 }}>
                  <div style={S.gearGroupLabel}>{cat}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {equipment[cat].map(item => (
                      <div
                        key={item.id}
                        onMouseEnter={() => setHov("seq_" + item.id)}
                        onMouseLeave={() => setHov(null)}
                        style={{ ...S.listRow, background: hov === "seq_" + item.id ? C.surfaceAlt : "transparent" }}
                      >
                        <div>
                          <span style={{ fontSize: 13, color: C.text, fontWeight: 300 }}>{item.name}</span>
                          {item.note && <span style={{ fontSize: 11, color: C.muted, marginLeft: 10 }}>{item.note}</span>}
                        </div>
                        {hov === "seq_" + item.id && (
                          <button onClick={() => removeEquipItem(cat, item.id)} style={S.trashBtn}>
                            <TrashIcon />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 18, marginTop: 8 }}>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em", color: C.muted, marginBottom: 12 }}>
                  Add Equipment
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {!showNewCat ? (
                      <select value={eqCat} onChange={e => setEqCat(e.target.value)} style={{ ...inp, fontSize: 12, padding: "8px 12px", flex: 1, appearance: "auto" }}>
                        <option value="">Select category...</option>
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    ) : (
                      <input value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="New category name..." style={{ ...inp, fontSize: 12, padding: "8px 12px", flex: 1 }} />
                    )}
                    <button onClick={() => setShowNewCat(!showNewCat)} style={{ ...btnS, padding: "8px 10px", fontSize: 10 }}>
                      {showNewCat ? "Existing" : "+ New"}
                    </button>
                  </div>
                  <input value={eqName} onChange={e => setEqName(e.target.value)} placeholder="Equipment name..." style={{ ...inp, fontSize: 12, padding: "8px 12px" }} />
                  <input value={eqNote} onChange={e => setEqNote(e.target.value)} placeholder="Description (optional)..." style={{ ...inp, fontSize: 12, padding: "8px 12px" }} />
                  <button
                    onClick={addEquipItem}
                    style={{ ...btnS, padding: "9px 16px", fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", gap: 5, width: "100%", color: C.amber, borderColor: `${C.amber}40` }}
                  >
                    <PlusIcon /> Add Equipment
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === "crew" && (
            <CrewRosterEditor crewRoster={crewRoster} setCrewRoster={setCrewRoster} />
          )}
          {activeTab === "storage" && (
            <ListManager
              items={storageOpts}
              setter={setStorageOpts}
              placeholder="Add storage location..."
              hov={hov}
              setHov={setHov}
              listInput={listInput}
              setListInput={setListInput}
              addListItem={addListItem}
              removeListItem={removeListItem}
            />
          )}
          {activeTab === "transport" && (
            <ListManager
              items={transportOpts}
              setter={setTransportOpts}
              placeholder="Add transport method..."
              hov={hov}
              setHov={setHov}
              listInput={listInput}
              setListInput={setListInput}
              addListItem={addListItem}
              removeListItem={removeListItem}
            />
          )}
          {activeTab === "roles" && (
            <div>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>Roles a crew member can be assigned on a Call Sheet (Docs &amp; Files).</div>
              <ListManager
                items={roleOpts}
                setter={setRoleOpts}
                placeholder="Add a role, e.g. Head Rig operator..."
                hov={hov}
                setHov={setHov}
                listInput={listInput}
                setListInput={setListInput}
                addListItem={addListItem}
                removeListItem={removeListItem}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ListManager — flat list editor for storage / transport
═══════════════════════════════════════════════════════════════ */
function ListManager({ items, setter, placeholder, hov, setHov, listInput, setListInput, addListItem, removeListItem }) {
  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2, marginBottom: 14 }}>
        {items.map(item => (
          <div
            key={item}
            onMouseEnter={() => setHov("li_" + item)}
            onMouseLeave={() => setHov(null)}
            style={{ ...S.listRow, background: hov === "li_" + item ? C.surfaceAlt : "transparent" }}
          >
            <span style={{ fontSize: 13, color: C.text, fontWeight: 300 }}>{item}</span>
            {hov === "li_" + item && (
              <button onClick={() => removeListItem(setter, item)} style={S.trashBtn}>
                <TrashIcon />
              </button>
            )}
          </div>
        ))}
        {items.length === 0 && (
          <div style={{ fontSize: 12, color: `${C.muted}66`, padding: 12, textAlign: "center" }}>No items yet</div>
        )}
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <input
          value={listInput}
          onChange={e => setListInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") addListItem(setter); }}
          placeholder={placeholder}
          style={{ ...inp, fontSize: 12, padding: "8px 12px" }}
        />
        <button onClick={() => addListItem(setter)} style={{ ...btnS, padding: "8px 14px", fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
          <PlusIcon /> Add
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   CrewRosterEditor — name + email + phone + receivesAllUpdates rows
═══════════════════════════════════════════════════════════════ */
function CrewRosterEditor({ crewRoster, setCrewRoster }) {
  const [hov, setHov] = useState(null);
  const add = () => setCrewRoster(prev => [...prev, { name: "", email: "", phone: "", receivesAllUpdates: false }]);
  const update = (idx, field, val) => setCrewRoster(prev => prev.map((c, i) => i === idx ? { ...c, [field]: val } : c));
  const remove = (idx) => setCrewRoster(prev => prev.filter((_, i) => i !== idx));

  // Count names (case-insensitive) so duplicates can be flagged without blocking typing.
  const nameCounts = {};
  crewRoster.forEach(c => {
    const k = (c.name || "").trim().toLowerCase();
    if (k) nameCounts[k] = (nameCounts[k] || 0) + 1;
  });

  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
        {crewRoster.map((c, idx) => {
          // Warn (don't block) when a member set to receive updates has no email, or a name is duplicated.
          const needsEmail = !!c.receivesAllUpdates && !(c.email || "").trim();
          const dupName = (c.name || "").trim() && nameCounts[(c.name || "").trim().toLowerCase()] > 1;
          const warn = needsEmail || dupName;
          return (
          <div
            key={idx}
            onMouseEnter={() => setHov("cr_" + idx)}
            onMouseLeave={() => setHov(null)}
            style={{ border: `1px solid ${warn ? C.amber : C.border}`, borderRadius: 6, padding: 10, background: C.surface }}
          >
            <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
              <input value={c.name} onChange={e => update(idx, "name", e.target.value)} placeholder="Name" style={{ ...inp, fontSize: 12, padding: "7px 10px", flex: 1 }} />
              {hov === "cr_" + idx && (
                <button onClick={() => remove(idx)} style={S.trashBtn}>
                  <TrashIcon />
                </button>
              )}
            </div>
            <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
              <input value={c.email} onChange={e => update(idx, "email", e.target.value)} placeholder="email@..." style={{ ...inp, fontSize: 12, padding: "7px 10px", flex: 1 }} />
              <input value={c.phone} onChange={e => update(idx, "phone", e.target.value)} placeholder="Phone" style={{ ...inp, fontSize: 12, padding: "7px 10px", flex: 1 }} />
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.muted, cursor: "pointer" }}>
              <input type="checkbox" checked={!!c.receivesAllUpdates} onChange={e => update(idx, "receivesAllUpdates", e.target.checked)} />
              Receives all job updates
            </label>
            {warn && (
              <div style={{ fontSize: 10.5, color: C.amber, marginTop: 6 }}>
                {needsEmail && <div>No email — won't get job/calendar updates.</div>}
                {dupName && <div>Duplicate name — another crew member has this name.</div>}
              </div>
            )}
          </div>
          );
        })}
      </div>
      <button onClick={add} style={{ ...btnS, padding: "8px 14px", fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
        <PlusIcon /> Add Crew
      </button>
    </div>
  );
}

/* ══ Icons (inline SVGs) ══ */
const PlusIcon = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M8 3v10M3 8h10" /></svg>
);
const XIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 4l8 8M12 4l-8 8" /></svg>
);
const TrashIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" /></svg>
);
const CheckIcon = ({ color }) => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="2"><path d="M3 8.5l3.5 3.5L13 5" /></svg>
);
const MapPinIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
);
const SettingsIcon = ({ color }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={color || "currentColor"} strokeWidth="1.5">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
  </svg>
);

/* ══ Style objects ══ */
const S = {
  controlsRow: { display: "flex", alignItems: "center", marginBottom: 12 },
  settingsBtn: { ...btnS, padding: "6px 12px", fontSize: 11, display: "flex", alignItems: "center", gap: 6 },

  card: { border: `1px solid ${C.border}`, borderRadius: 6, overflow: "hidden", background: C.surface },
  header: { width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", border: "none", cursor: "pointer", fontFamily: font },

  briefStrip: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", borderBottom: `1px solid ${C.border}` },
  briefCell: { padding: "14px 18px" },
  briefLabel: { fontSize: 9, textTransform: "uppercase", letterSpacing: "0.15em", color: C.muted, marginBottom: 4 },
  briefValue: { fontSize: 13, fontWeight: 400 },

  gearGroupLabel: { fontSize: 9, color: `${C.muted}99`, textTransform: "uppercase", letterSpacing: "0.15em", marginBottom: 8 },
  gearItem: { display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 4 },
  gearDot: { width: 8, height: 8, borderRadius: "50%", background: C.blue, flexShrink: 0 },
  gearCheckbox: {
    width: 18, height: 18, borderRadius: 3, flexShrink: 0,
    border: `1px solid ${C.border}`, background: "transparent",
    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
    padding: 0,
  },
  emptyHint: { fontSize: 12, color: `${C.muted}99`, textAlign: "center", padding: 12 },

  divider: { height: 1, background: C.border },

  tcRow: { padding: "20px 18px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 },
  toggleChip: { padding: "7px 14px", fontSize: 12, fontWeight: 300, border: `1px solid ${C.border}`, borderRadius: 6, cursor: "pointer", background: "transparent", fontFamily: font },
  crewChip: {
    display: "inline-block", padding: "4px 12px", borderRadius: 4,
    fontSize: 11, background: `${C.green}12`, color: C.green, border: `1px solid ${C.green}30`,
  },
  emptyInlineMuted: { fontSize: 12, color: `${C.muted}99`, fontStyle: "italic" },

  locationBlock: { padding: "16px 18px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 },
  locationVenue: { fontSize: 14, color: C.text, fontWeight: 400, marginBottom: 4 },
  locationLine: { fontSize: 12, color: C.muted },
  mapLink: {
    flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 6,
    padding: "5px 12px", borderRadius: 6,
    border: `1px solid ${C.blue}30`, background: `${C.blue}10`, color: C.blue,
    fontSize: 11, textDecoration: "none", whiteSpace: "nowrap",
  },

  twoCol: { display: "grid", gridTemplateColumns: "1fr 1fr" },
  notesBody: { fontSize: 13, color: C.text, lineHeight: 1.7, margin: "6px 0 0", fontWeight: 300 },

  simpleChip: {
    display: "inline-flex", alignItems: "center", gap: 5,
    padding: "5px 10px", borderRadius: 4,
    background: C.surfaceAlt, border: `1px solid ${C.border}`,
    fontSize: 12, color: C.text, fontWeight: 300,
  },
  chipClose: { background: "none", border: "none", cursor: "pointer", color: C.danger, opacity: 0.6, padding: 0, marginLeft: 2, display: "flex" },

  /* Settings slide-over */
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 998, backdropFilter: "blur(2px)" },
  slideOver: {
    position: "fixed", top: 0, right: 0, bottom: 0, width: 440,
    background: C.bg, borderLeft: `1px solid ${C.border}`, zIndex: 999,
    display: "flex", flexDirection: "column",
    animation: "slideIn 0.25s ease", boxShadow: "-8px 0 30px rgba(0,0,0,0.15)",
  },
  slideHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 22px", borderBottom: `1px solid ${C.border}` },
  iconBtn: { background: "none", border: "none", cursor: "pointer", color: C.muted, padding: 4 },
  slideTab: { flex: 1, padding: "11px 0", fontSize: 11, fontWeight: 400, textTransform: "uppercase", letterSpacing: "0.1em", border: "none", cursor: "pointer", fontFamily: font },
  listRow: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 4 },
  trashBtn: { background: "none", border: "none", cursor: "pointer", color: C.danger, opacity: 0.6, padding: 2, display: "flex" },
};
